import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import styled from "styled-components/macro";
import { Prompt } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useAxiosInstance } from "../../fieldServicesApp/hooks/useAxiosInstance";
import { useApp } from "../../../AppProvider";
import { fmtDate, fmtPaValue, fmtResult, isPaParameter } from "./format";

const DRAWER_WIDTH = 480;

// Theme variants don't define palette.error, so use Material-UI's default
// destructive red (red 700) directly for the delete + discard actions.
const DESTRUCTIVE_COLOR = "#d32f2f";
const DESTRUCTIVE_HOVER = "#b71c1c";

const DrawerSurface = styled(Box)`
  width: ${DRAWER_WIDTH}px;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const Section = styled(Box)`
  padding: 12px 20px;
`;

const Form = styled(Box)`
  padding: 12px 20px 20px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 320px;
`;

const RecordsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 6px;
  max-height: 225px;
  overflow-y: auto;
  padding-right: 4px;
`;

const RecordCard = styled.button`
  appearance: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.palette.action.selected : theme.palette.background.paper};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.palette.primary.main : theme.palette.divider};
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: -1px;
  }
`;

const RecordCardLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
`;

const RecordCardValue = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const FormActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  gap: 8px;
`;

const FieldRow = styled(Box)`
  margin-bottom: 14px;
`;

// Whitelist of fields we send to PUT /wq-record/:dataNdx. Keep in sync
// with the editable list on the backend.
const EDITABLE_KEYS = [
  "parameter_ndx",
  "result_value",
  "is_nondetect",
  "aqsci_data_source_ndx",
  "aqsci_lab_ndx",
  "aqsci_equipment_ndx",
  "equipment_other_desc",
  "notes",
  "well_purged",
];

// Presence/absence results are stored as 1/0 but arrive from the API as
// strings ("1"/"0") since result_value is a numeric column. Normalize for
// the radio group.
const paRadioValue = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  return n === 1 ? "present" : n === 0 ? "not_present" : "";
};

const buildFormState = (record) => {
  if (!record) return null;
  const out = {};
  EDITABLE_KEYS.forEach((key) => {
    out[key] = record[key] ?? null;
  });
  // Edit the exactly-as-typed value (numeric → string, trailing zeros
  // intact) rather than the float mirror; the backend derives both columns
  // from what we send as result_value.
  out.result_value = record.result_value_entered ?? record.result_value ?? null;
  return out;
};

const isDirty = (form, original) => {
  if (!form || !original) return false;
  return EDITABLE_KEYS.some((key) => {
    const a = form[key];
    const b = original[key];
    if (a === null && b == null) return false;
    return a !== b;
  });
};

const useFsLookup = (axiosInstance, name, path, mapper) =>
  useQuery(
    [name],
    async () => {
      const { data } = await axiosInstance.get(path);
      return data.map(mapper);
    },
    {
      staleTime: 5 * 60 * 1000,
      enabled: !!axiosInstance,
      refetchOnWindowFocus: false,
    }
  );

const WqEditDrawer = ({
  open,
  onClose,
  wellNdx,
  wellName,
  collectTimestamp,
  initialParameterNdx,
  processed,
  onSaved,
}) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const { doToast } = useApp();

  // --- lookups (mirror what WaterQualityPage fetches) ---
  // Shares the ["wqParameters"] cache key with WaterQualityPage's lookup —
  // the mapped shape must stay a superset of what both consumers read
  // ({ id, label, name, dataentry_primary }), otherwise whichever fetches
  // first strips fields the other needs (missing dataentry_primary made the
  // FS entry form treat every parameter as optional).
  const params = useFsLookup(
    axiosInstance,
    "wqParameters",
    "/lookup/ui-list-wq-parameters",
    (i) => ({
      id: i.parameter_ndx,
      label: i.param_abbrev,
      name: i.parameter,
      dataentry_primary: i.dataentry_primary,
    })
  );
  const sources = useFsLookup(
    axiosInstance,
    "wqDataSources",
    "/lookup/ui-list-wq-sources",
    (i) => ({ id: i.aqsci_data_source_ndx, label: i.aqsci_data_source_desc })
  );
  const labs = useFsLookup(
    axiosInstance,
    "wqLabs",
    "/lookup/ui-list-wq-labs",
    (i) => ({ id: i.aqsci_lab_ndx, label: i.aqsci_lab_desc })
  );
  const equipment = useFsLookup(
    axiosInstance,
    "wqEquipment",
    "/lookup/ui-list-wq-equipment",
    (i) => ({
      id: i.aqsci_equipment_ndx,
      label: i.aqsci_equipment_desc,
      is_other: i.is_other,
    })
  );

  // --- records for this event ---
  const eventQueryKey = useMemo(
    () => ["wq-event", wellNdx, collectTimestamp],
    [wellNdx, collectTimestamp]
  );
  const {
    data: records = [],
    isLoading: recordsLoading,
    isError: recordsErrored,
    refetch,
  } = useQuery(
    eventQueryKey,
    async () => {
      const { data } = await axiosInstance.get("/wq-event", {
        params: { well_ndx: wellNdx, collect_timestamp: collectTimestamp },
      });
      return data;
    },
    {
      enabled: open && !!axiosInstance && !!wellNdx && !!collectTimestamp,
      staleTime: 0,
      // Don't re-pull the event when the user tabs away and back — the
      // drawer mid-edit shouldn't be reloading data underneath the sidebar.
      refetchOnWindowFocus: false,
      // After save/delete refetch, keep the previous records visible so the
      // parameter cards don't flash to a spinner before the fresh data lands.
      keepPreviousData: true,
    }
  );

  // Set of parameter_ndx values already present in this event. Used to
  // filter the Parameter dropdown so a user can't accidentally change a
  // record to a parameter that already has its own reading at this event
  // (which would silently create a duplicate at the same well + timestamp).
  const usedParameterNdxs = useMemo(
    () => new Set(records.map((r) => r.parameter_ndx)),
    [records]
  );

  // The de_wq response holds raw auth0 IDs in edited_by; the page-level
  // wq_data_processed view already resolves those to auth0_name. Reuse it
  // by indexing processed rows by data_ndx (de_wq.data_ndx == processed
  // data_ndx for non-historical rows).
  const editMetaByDataNdx = useMemo(() => {
    const m = new Map();
    processed?.forEach((r) => {
      if (r.data_ndx >= 900000000) return;
      m.set(r.data_ndx, { edited_by: r.edited_by, edited_on: r.edited_on });
    });
    return m;
  }, [processed]);

  // --- selection + form state ---
  const [selectedNdx, setSelectedNdx] = useState(null);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  // Pending action when the user has unsaved edits. Drives the
  // discard-confirm dialog. Shape:
  //   null
  //   { kind: "close" }                      — close-button or X click
  //   { kind: "create" }                     — Add parameter clicked
  //   { kind: "select", dataNdx: number }    — clicked a different record
  const [pendingAction, setPendingAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // True while the user is filling out a brand-new parameter row that
  // hasn't been POSTed yet. selectedNdx is null while creating; on save
  // it becomes the freshly-returned data_ndx.
  const [isCreating, setIsCreating] = useState(false);

  const dirty = isDirty(form, original);

  // Auto-select once records load. Prefer the record matching
  // initialParameterNdx (so deep-links land on the right parameter); fall
  // back to the first record. Skip when the user is mid-create (selectedNdx
  // is null in that case but we don't want to clobber the new draft).
  useEffect(() => {
    if (!open) return;
    if (isCreating) return;
    if (selectedNdx != null) return;
    if (records.length === 0) return;
    const target =
      (initialParameterNdx != null &&
        records.find((r) => r.parameter_ndx === initialParameterNdx)) ||
      records[0];
    setSelectedNdx(target.data_ndx);
    const fresh = buildFormState(target);
    setForm(fresh);
    setOriginal(fresh);
  }, [open, records, selectedNdx, initialParameterNdx, isCreating]);

  // Reset all local state on close.
  useEffect(() => {
    if (open) return;
    setSelectedNdx(null);
    setForm(null);
    setOriginal(null);
    setPendingAction(null);
    setConfirmDelete(false);
    setIsCreating(false);
  }, [open]);

  // Warn the user if they try to close the tab / refresh / navigate the
  // browser away while the form is dirty. <Prompt> handles in-app routing
  // separately below.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const selectRecord = useCallback(
    (dataNdx) => {
      const rec = records.find((r) => r.data_ndx === dataNdx);
      if (!rec) return;
      setIsCreating(false);
      setSelectedNdx(rec.data_ndx);
      const fresh = buildFormState(rec);
      setForm(fresh);
      setOriginal(fresh);
    },
    [records]
  );

  const handleListClick = (dataNdx) => {
    if (!isCreating && dataNdx === selectedNdx) return;
    if (dirty) {
      setPendingAction({ kind: "select", dataNdx });
      return;
    }
    selectRecord(dataNdx);
  };

  // Begin creating a new row. Use the same defaults as the FS create form
  // (WaterQualityForm.jsx defaultValues) so behavior is consistent with
  // creating a brand-new event — user has to pick the event-level fields
  // explicitly rather than silently inheriting from another row.
  const handleStartCreate = useCallback(() => {
    const draft = {
      parameter_ndx: null,
      result_value: null,
      is_nondetect: false,
      aqsci_data_source_ndx: null,
      aqsci_lab_ndx: null,
      aqsci_equipment_ndx: null,
      equipment_other_desc: null,
      notes: "",
      well_purged: true,
    };
    setIsCreating(true);
    setSelectedNdx(null);
    setForm(draft);
    setOriginal(draft);
  }, []);

  const handleCancelCreate = useCallback(() => {
    // Drop the draft. The auto-select effect will land on the first
    // existing record (or stay empty if there are none).
    setIsCreating(false);
    setSelectedNdx(null);
    setForm(null);
    setOriginal(null);
  }, []);

  const handleConfirmDiscard = () => {
    if (!pendingAction) return;
    if (pendingAction.kind === "create") {
      handleStartCreate();
    } else if (pendingAction.kind === "select") {
      selectRecord(pendingAction.dataNdx);
    }
    setPendingAction(null);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    setForm(original);
  };

  const handleSave = async () => {
    if (!form) return;
    if (!isCreating && !selectedNdx) return;
    // Mirror the FS form's WQFormSchema:
    //   - parameter_ndx is required (positiveInt)
    //   - aqsci_data_source_ndx is required (positiveInt)
    //   - aqsci_equipment_ndx is required (positiveInt)
    //   - aqsci_lab_ndx and equipment_other_desc are optional / nullable
    //   - non-presence/absence + is_nondetect requires result_value
    if (isCreating && !form.parameter_ndx) {
      doToast("error", "Pick a parameter before saving.");
      return;
    }
    if (!form.aqsci_data_source_ndx) {
      doToast("error", "Source is required.");
      return;
    }
    if (!form.aqsci_equipment_ndx) {
      doToast("error", "Equipment is required.");
      return;
    }
    const isPa = isPaParameter(form.parameter_ndx);
    if (form.result_value === null || form.result_value === undefined) {
      doToast(
        "error",
        isPa
          ? "Pick Present or Not Present before saving."
          : form.is_nondetect
          ? "Enter the detection limit as a result value when marking non-detect."
          : "Enter a result value before saving."
      );
      return;
    }
    if (
      typeof form.result_value === "string" &&
      isNaN(Number(form.result_value))
    ) {
      doToast("error", "Result value must be a number.");
      return;
    }
    setSaving(true);
    try {
      if (isCreating) {
        const { data: created } = await axiosInstance.post("/wq-record", {
          well_ndx: wellNdx,
          collect_timestamp: collectTimestamp,
          ...form,
        });
        doToast("success", "Record added.");
        const refetched = await refetch();
        queryClient.invalidateQueries("wq-data-processed");
        queryClient.invalidateQueries("wq-data-stats-by-parameter");
        queryClient.invalidateQueries("wq-data-summary");
        // Find the just-created row by parameter_ndx — we already enforce
        // uniqueness of (well, timestamp, parameter), so there's exactly
        // one match in the refetched event and it has to be ours. This
        // sidesteps any data_ndx type-coercion mismatch between the POST
        // response and the GET payload.
        const newRow =
          refetched.data?.find((r) => r.parameter_ndx === form.parameter_ndx) ||
          created;
        // Set selection + form before flipping isCreating off — that way
        // the auto-select effect always sees a non-null selectedNdx when
        // it observes isCreating=false, and won't grab the first record.
        if (newRow?.data_ndx != null) {
          const fresh = buildFormState(newRow);
          setSelectedNdx(newRow.data_ndx);
          setForm(fresh);
          setOriginal(fresh);
        }
        setIsCreating(false);
      } else {
        await axiosInstance.put(`/wq-record/${selectedNdx}`, form);
        // Clear dirty by promoting the just-sent form values to the new
        // "original" — closing the drawer no longer triggers the discard
        // prompt, and Save / Reset disable correctly until the next edit.
        setOriginal(form);
        doToast("success", "Record saved.");
        await refetch();
        queryClient.invalidateQueries("wq-data-processed");
        queryClient.invalidateQueries("wq-data-stats-by-parameter");
        queryClient.invalidateQueries("wq-data-summary");
      }
      if (onSaved) onSaved();
    } catch (e) {
      doToast(
        "error",
        e?.response?.data?.error || e.message || "Failed to save record."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNdx) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/wq-record/${selectedNdx}`);
      setConfirmDelete(false);
      doToast("success", "Record deleted.");
      // Refetch first so we read selection state from the post-delete
      // truth. If we cleared selectedNdx before refetching, the auto-
      // select effect would resurrect the just-deleted row from the
      // pre-refetch records (kept around by keepPreviousData) and the
      // form would refill with its data.
      const refetched = await refetch();
      queryClient.invalidateQueries("wq-data-processed");
      queryClient.invalidateQueries("wq-data-stats-by-parameter");
      queryClient.invalidateQueries("wq-data-summary");
      const fresh = refetched.data || [];
      const next = fresh[0];
      if (next) {
        const nextForm = buildFormState(next);
        setSelectedNdx(next.data_ndx);
        setForm(nextForm);
        setOriginal(nextForm);
      } else {
        setSelectedNdx(null);
        setForm(null);
        setOriginal(null);
      }
      if (onSaved) onSaved();
    } catch (e) {
      doToast(
        "error",
        e?.response?.data?.error || e.message || "Failed to delete record."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (dirty) {
      setPendingAction({ kind: "close" });
      return;
    }
    onClose();
  };

  const handleConfirmClose = () => {
    setPendingAction(null);
    onClose();
  };

  const lookupsLoading =
    params.isLoading ||
    sources.isLoading ||
    labs.isLoading ||
    equipment.isLoading;

  const selectedEquipment = equipment.data?.find(
    (e) => e.id === form?.aqsci_equipment_ndx
  );
  const selectedParameter = params.data?.find(
    (p) => p.id === form?.parameter_ndx
  );

  return (
    <>
      <Prompt
        when={dirty}
        message="You have unsaved edits to this record. Discard them and continue?"
      />
      <Drawer anchor="right" open={open} onClose={handleRequestClose}>
        <DrawerSurface>
          <Header>
            <Box>
              <Typography variant="overline" color="textSecondary">
                Edit Sampling Event
              </Typography>
              <Typography variant="h6">{wellName || "—"}</Typography>
              <Typography variant="caption" color="textSecondary">
                {fmtDate(collectTimestamp, { pattern: "yyyy-MM-dd HH:mm" })}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleRequestClose}>
              <CloseIcon />
            </IconButton>
          </Header>

          <Section>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ gap: 8 }}
            >
              <Typography variant="subtitle2">Records in this event</Typography>
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  if (dirty && !isCreating) {
                    // Unsaved edits in the existing record — route through
                    // the dirty-guard; on confirm we'll start the draft.
                    setPendingAction({ kind: "create" });
                    return;
                  }
                  handleStartCreate();
                }}
                disabled={
                  isCreating ||
                  !params.data ||
                  // Every parameter already in use → nothing to add.
                  params.data.every((p) => usedParameterNdxs.has(p.id))
                }
              >
                Add parameter
              </Button>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginBottom: 6 }}
            >
              Each parameter is saved individually — pick one below to edit.
            </Typography>
            {recordsLoading ? (
              <CircularProgress size={20} />
            ) : recordsErrored ? (
              <Box>
                <Typography variant="body2" color="error" gutterBottom>
                  Failed to load records for this event.
                </Typography>
                <Button size="small" onClick={() => refetch()}>
                  Try again
                </Button>
              </Box>
            ) : records.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No records found for this event.
              </Typography>
            ) : (
              <RecordsGrid>
                {records.map((r) => {
                  const param = params.data?.find(
                    (p) => p.id === r.parameter_ndx
                  );
                  const label = param?.label || `#${r.parameter_ndx}`;
                  const isSelected = r.data_ndx === selectedNdx;
                  const isPa = isPaParameter(r.parameter_ndx);
                  return (
                    <RecordCard
                      key={r.data_ndx}
                      type="button"
                      $selected={isSelected}
                      onClick={() => handleListClick(r.data_ndx)}
                    >
                      <RecordCardLabel>{label}</RecordCardLabel>
                      <RecordCardValue>
                        {isPa ? (
                          fmtPaValue(r.result_value)
                        ) : (
                          <>
                            {fmtResult(
                              r.result_value_entered ?? r.result_value,
                              { fallback: "—" }
                            )}
                            {r.is_nondetect && (
                              <Chip
                                size="small"
                                label="ND"
                                style={{ height: 16, fontSize: 10 }}
                              />
                            )}
                          </>
                        )}
                      </RecordCardValue>
                    </RecordCard>
                  );
                })}
              </RecordsGrid>
            )}
          </Section>

          <Divider />

          <Form>
            {!form ? (
              <Typography variant="body2" color="textSecondary">
                Select a record to edit.
              </Typography>
            ) : lookupsLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Box mb={1.5}>
                  <Typography variant="subtitle2">
                    {isCreating
                      ? "New record"
                      : selectedParameter?.name ||
                        selectedParameter?.label ||
                        "Record"}
                  </Typography>
                  {!isCreating &&
                    (() => {
                      const meta = editMetaByDataNdx.get(selectedNdx);
                      if (!meta?.edited_on) return null;
                      return (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          style={{ fontStyle: "italic" }}
                        >
                          Edited{" "}
                          {fmtDate(meta.edited_on, { pattern: "yyyy-MM-dd" })}
                          {meta.edited_by ? ` · ${meta.edited_by}` : ""}
                        </Typography>
                      );
                    })()}
                  {isCreating && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      style={{ fontStyle: "italic" }}
                    >
                      Pick a parameter and fill in the result, then set the
                      source / lab / equipment as needed.
                    </Typography>
                  )}
                </Box>

                <FieldRow>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Parameter</InputLabel>
                    <Select
                      label="Parameter"
                      value={form.parameter_ndx ?? ""}
                      onChange={(e) =>
                        updateField("parameter_ndx", e.target.value)
                      }
                    >
                      {(params.data || [])
                        // Hide parameters that already have a reading at this
                        // event, but always keep the currently-selected one
                        // so the form's value renders correctly.
                        .filter(
                          (p) =>
                            p.id === form.parameter_ndx ||
                            !usedParameterNdxs.has(p.id)
                        )
                        .map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name && p.name !== p.label
                              ? `${p.label} — ${p.name}`
                              : p.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </FieldRow>

                {isPaParameter(form.parameter_ndx) ? (
                  <FieldRow>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Result
                    </Typography>
                    <RadioGroup
                      row
                      value={paRadioValue(form.result_value)}
                      onChange={(e) => {
                        if (e.target.value === "present") {
                          setForm((prev) => ({
                            ...prev,
                            result_value: 1,
                            is_nondetect: false,
                          }));
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            result_value: 0,
                            is_nondetect: true,
                          }));
                        }
                      }}
                    >
                      <FormControlLabel
                        value="present"
                        control={<Radio />}
                        label="Present"
                      />
                      <FormControlLabel
                        value="not_present"
                        control={<Radio />}
                        label="Not Present"
                      />
                    </RadioGroup>
                  </FieldRow>
                ) : (
                  <>
                    <FieldRow>
                      <TextField
                        label="Result"
                        type="number"
                        value={form.result_value ?? ""}
                        // Keep the raw string — Number("2.0") would collapse
                        // to 2 and lose the trailing zero that records the
                        // measurement's precision. The numeric DB column
                        // stores the value exactly as typed.
                        onChange={(e) =>
                          updateField(
                            "result_value",
                            e.target.value === "" ? null : e.target.value
                          )
                        }
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </FieldRow>

                    <FieldRow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!form.is_nondetect}
                            onChange={(e) =>
                              updateField("is_nondetect", e.target.checked)
                            }
                          />
                        }
                        label="Non-detect"
                      />
                    </FieldRow>
                  </>
                )}

                <FieldRow>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Source</InputLabel>
                    <Select
                      label="Source"
                      value={form.aqsci_data_source_ndx ?? ""}
                      onChange={(e) =>
                        updateField("aqsci_data_source_ndx", e.target.value)
                      }
                    >
                      {(sources.data || []).map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldRow>

                <FieldRow>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Lab</InputLabel>
                    <Select
                      label="Lab"
                      value={form.aqsci_lab_ndx ?? ""}
                      onChange={(e) =>
                        updateField(
                          "aqsci_lab_ndx",
                          e.target.value === "" ? null : e.target.value
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(labs.data || []).map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldRow>

                <FieldRow>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Equipment</InputLabel>
                    <Select
                      label="Equipment"
                      value={form.aqsci_equipment_ndx ?? ""}
                      onChange={(e) => {
                        const nextNdx = e.target.value;
                        const nextEquip = equipment.data?.find(
                          (eq) => eq.id === nextNdx
                        );
                        // Auto-clear the "Other" description when switching
                        // to a non-Other equipment so stale text doesn't
                        // linger in a hidden field.
                        setForm((prev) => ({
                          ...prev,
                          aqsci_equipment_ndx: nextNdx,
                          equipment_other_desc: nextEquip?.is_other
                            ? prev.equipment_other_desc
                            : null,
                        }));
                      }}
                    >
                      {(equipment.data || []).map((e) => (
                        <MenuItem key={e.id} value={e.id}>
                          {e.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldRow>

                {selectedEquipment?.is_other && (
                  <FieldRow>
                    <TextField
                      label="Equipment Description (Other)"
                      value={form.equipment_other_desc ?? ""}
                      onChange={(e) =>
                        updateField("equipment_other_desc", e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  </FieldRow>
                )}

                <FieldRow>
                  <TextField
                    label="Notes"
                    value={form.notes ?? ""}
                    onChange={(e) => updateField("notes", e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    variant="outlined"
                  />
                </FieldRow>

                <FieldRow>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form.well_purged}
                        onChange={(e) =>
                          updateField("well_purged", e.target.checked)
                        }
                      />
                    }
                    label="Well purged"
                  />
                </FieldRow>
              </>
            )}
          </Form>

          {form && (
            <FormActions>
              {isCreating ? (
                <span /> /* spacer to keep right-aligned buttons in place */
              ) : (
                <Button
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  style={{
                    color: saving ? undefined : DESTRUCTIVE_COLOR,
                  }}
                >
                  Delete
                </Button>
              )}
              <Box display="flex" gridGap={8}>
                <Button
                  onClick={isCreating ? handleCancelCreate : handleClear}
                  disabled={(!isCreating && !dirty) || saving}
                >
                  {isCreating ? "Cancel" : "Reset"}
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  color="primary"
                  disabled={!dirty || saving}
                >
                  {saving ? "Saving…" : isCreating ? "Add" : "Save"}
                </Button>
              </Box>
            </FormActions>
          )}
        </DrawerSurface>
      </Drawer>

      {/* Dirty-state guard: confirm before discarding edits */}
      <Dialog
        open={pendingAction != null}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
      >
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved edits to this record. Continuing will lose them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)}>Cancel</Button>
          <Button
            onClick={
              pendingAction?.kind === "close"
                ? handleConfirmClose
                : handleConfirmDiscard
            }
            variant="contained"
            style={{
              backgroundColor: DESTRUCTIVE_COLOR,
              color: "#fff",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = DESTRUCTIVE_HOVER)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = DESTRUCTIVE_COLOR)
            }
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
      >
        <DialogTitle>
          Delete the{" "}
          <strong>
            {selectedParameter?.name || selectedParameter?.label || "this"}
          </strong>{" "}
          record?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will hide the record from the viewer. It can be restored later
            by clearing the deleted flag in the database.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={saving}
            style={{
              backgroundColor: saving ? undefined : DESTRUCTIVE_COLOR,
              color: saving ? undefined : "#fff",
            }}
            onMouseOver={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = DESTRUCTIVE_HOVER;
              }
            }}
            onMouseOut={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = DESTRUCTIVE_COLOR;
              }
            }}
          >
            {saving ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WqEditDrawer;
